import { Action, Card, SimpleTooltip } from 'corso-admin-ui';
import { CrewMerchantUi, trackingStatusCodeName } from 'corso-types';
import { formatter } from 'corso-web-lib';
import { ComponentProps, ReactNode, useState } from 'react';
import ClipboardButton from '~/components/ClipboardButton';
import CarrierIcon from '~/components/tracking/CarrierIcon';
import { ShipmentTrackingModal } from '~/components/tracking/ShipmentTrackingModal';

type Shipment = CrewMerchantUi.OrderShipment;

export function ShipmentTracking({
  shipment,
  renderLineItems,
  paginator,
}: {
  shipment: Shipment;
  renderLineItems?: ComponentProps<
    typeof ShipmentTrackingModal
  >['renderLineItems'];
  paginator?: ReactNode;
}) {
  const [showDetails, setShowDetails] = useState(false);
  const { carrier, status } = shipment.tracker;

  const trackingUrl =
    carrier.trackingUrl ??
    `https://www.google.com/search?q=${carrier.trackingCode}`;

  const latestDetailDate = shipment.tracker.details.sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  )[0]?.date;

  // ? Only show the tracking eye if there is more than one tracking detail
  const hasMoreThanOneDetail = shipment.tracker.details.length > 1;

  return (
    <div className="flex flex-col">
      <Card.Line>
        <Card.Heading>Shipment</Card.Heading>

        <Action
          icon={hasMoreThanOneDetail ? 'eye' : 'warning'}
          variant="ghost"
          accessibilityLabel="View Shipment Details"
          onClick={() => setShowDetails(true)}
        />
      </Card.Line>

      <div className="flex items-center space-x-2">
        <CarrierIcon carrierName={carrier.name} />
      </div>

      <div className="flex flex-col">
        <p className="text-xs text-neutral-500">
          {trackingStatusCodeName[status]}
        </p>
        <div className="flex items-center justify-between">
          <Action
            variant="link"
            target="_blank"
            className="text-xs"
            to={trackingUrl}
          >
            {carrier.trackingCode}
          </Action>
          {carrier.trackingUrl && (
            <SimpleTooltip content="Copy tracking URL">
              <ClipboardButton
                accessibilityLabel="Copy tracking URL"
                onClick={() => trackingUrl}
                variant="ghost"
              />
            </SimpleTooltip>
          )}
        </div>
        {latestDetailDate && (
          <p className="text-xs text-neutral-500">
            {latestDetailDate &&
              `Last scan ${formatter.relativeTimeFromNow(new Date(latestDetailDate))}`}
          </p>
        )}
      </div>

      <ShipmentTrackingModal
        shipment={shipment}
        show={showDetails}
        onClose={() => setShowDetails(false)}
        renderLineItems={renderLineItems}
        paginator={paginator}
      />
    </div>
  );
}
